import React from 'react';
import Layout from '../components/layout/Layout';
import type { AuthState } from '../types';
import SEOHead from '../components/SEOHead';

interface ContactProps {
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
}

const Contact: React.FC<ContactProps> = ({ authState, setAuthState }) => {
  return (
    <Layout authState={authState} setAuthState={setAuthState}>
      <SEOHead
        title="Contact Us"
        description="Get in touch with MarketEye for support, feature requests, or general inquiries."
        canonicalUrl="/contact"
      />
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">

        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Email */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-700 mb-2">
                <a href="mailto:ankitdaf@gmail.com" className="text-blue-600 hover:text-blue-700 font-medium">
                  ankitdaf@gmail.com
                </a>
              </p>
              <p className="text-sm text-gray-500">
                We typically respond within 24 hours
              </p>
            </div>

            {/* Support */}
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">General Inquiries</h3>
              <p className="text-gray-700 mb-2">
                Need help or have questions ?
              </p>
              <p className="text-sm text-gray-500">
                Reach out and we'll get back ASAP
              </p>
            </div>

            {/* Feature Requests */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Feature Requests</h3>
              <p className="text-gray-700 mb-2">
                Have an idea or a suggestion?
              </p>
              <p className="text-sm text-gray-500">
                We'd love to hear it!
              </p>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About MarketEye</h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
              MarketEye arose out of a desire to manage investments smarter. There are many comprehensive
              tools out there, but few that are convenient to use repeatedly and consistently. Our goal is
              to act like a financial copilot so that making informed decisions takes less time and effort.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do I get started with MarketEye?</h3>
              <p className="text-gray-700">
                Simply sign up with your Google account, upload your portfolio holdings via CSV, and start setting up alerts and rules.
                Our <a href="/know-more" className="text-blue-600 hover:text-blue-700">Getting Started guide</a> has detailed instructions.
              </p>
            </div>

            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What file formats do you support for portfolio upload?</h3>
              <p className="text-gray-700">
                We currently support CSV files exported from most major brokers. The file should contain columns for symbol, quantity,
                and average price. Check our documentation for the exact format requirements.
              </p>
            </div>

            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How do alerts and notifications work?</h3>
              <p className="text-gray-700">
                You can set up price alerts, percentage change notifications, and automated rules. Notifications are sent via email
                and can be configured in your account settings.
              </p>
            </div>

            <div className="pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is my data secure?</h3>
              <p className="text-gray-700">
                Yes, we take security seriously. All data is encrypted in transit and at rest. We use industry-standard security
                practices and never store sensitive information like passwords. Your portfolio data is only accessible to you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Contact; 